import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  const errorMessage = `
    ⚠️ Supabase Configuration Missing

    The application cannot start because Supabase environment variables are not configured.

    Missing variables:
    ${!supabaseUrl ? '- VITE_SUPABASE_URL\n' : ''}${!supabaseAnonKey ? '- VITE_SUPABASE_ANON_KEY\n' : ''}

    If you're the site owner, please configure these environment variables in your hosting platform:

    For Netlify:
    1. Go to your site dashboard at netlify.com
    2. Navigate to Site configuration > Environment variables
    3. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY
    4. Redeploy your site

    For local development:
    1. Create a .env file in the project root
    2. Add the required environment variables
    3. Restart your development server
  `;

  console.error(errorMessage);

  document.body.innerHTML = `
    <div style="
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #0a0a0a;
      color: #ffffff;
      font-family: system-ui, -apple-system, sans-serif;
      padding: 20px;
    ">
      <div style="
        max-width: 600px;
        background: #1a1a1a;
        border: 1px solid #333;
        border-radius: 8px;
        padding: 32px;
      ">
        <h1 style="
          color: #ef4444;
          font-size: 24px;
          margin: 0 0 16px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        ">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          Configuration Error
        </h1>

        <p style="color: #999; margin: 0 0 24px 0; line-height: 1.6;">
          The application cannot start because Supabase environment variables are not configured.
        </p>

        <div style="
          background: #0a0a0a;
          border: 1px solid #333;
          border-radius: 4px;
          padding: 16px;
          margin-bottom: 24px;
        ">
          <strong style="color: #fff; display: block; margin-bottom: 8px;">Missing variables:</strong>
          <ul style="margin: 0; padding-left: 20px; color: #999;">
            ${!supabaseUrl ? '<li>VITE_SUPABASE_URL</li>' : ''}
            ${!supabaseAnonKey ? '<li>VITE_SUPABASE_ANON_KEY</li>' : ''}
          </ul>
        </div>

        <details style="cursor: pointer;">
          <summary style="
            color: #fff;
            font-weight: 600;
            margin-bottom: 12px;
            user-select: none;
          ">
            How to fix this
          </summary>
          <div style="
            color: #999;
            line-height: 1.6;
            padding-left: 20px;
            margin-top: 12px;
          ">
            <strong style="color: #fff; display: block; margin-bottom: 8px;">For Netlify:</strong>
            <ol style="margin: 0 0 16px 0; padding-left: 20px;">
              <li>Go to your site dashboard at netlify.com</li>
              <li>Navigate to Site configuration → Environment variables</li>
              <li>Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY</li>
              <li>Trigger a new deployment</li>
            </ol>

            <strong style="color: #fff; display: block; margin-bottom: 8px;">For local development:</strong>
            <ol style="margin: 0; padding-left: 20px;">
              <li>Create a .env file in the project root</li>
              <li>Add the required environment variables</li>
              <li>Restart your development server</li>
            </ol>
          </div>
        </details>
      </div>
    </div>
  `;

  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storageKey: 'metanoia-auth',
    storage: {
      getItem: (key: string) => {
        try {
          return localStorage.getItem(key);
        } catch (error) {
          console.warn('Storage getItem failed:', error);
          return null;
        }
      },
      setItem: (key: string, value: string) => {
        try {
          localStorage.setItem(key, value);
        } catch (error) {
          console.warn('Storage setItem failed:', error);
        }
      },
      removeItem: (key: string) => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn('Storage removeItem failed:', error);
        }
      }
    }
  },
  global: {
    headers: {
      'X-Client-Info': 'metanoia-app',
    },
  },
});
